import React, { useEffect, useMemo, useRef } from "react";

import {
  Alert,
  AlertTitle,
  Box,
  Card,
  CardContent,
  CardHeader,
  Link,
  Pagination,
  Skeleton,
  Toolbar,
  Typography,
} from "@mui/material";
import { useLanguage } from "@tlc/ls2pac/components/Language";
import { useNumberFormat } from "../../../../hooks/useNumberFormat";
import { useBritannicaSearch } from "./BritannicaSearchProvider";
import { BritannicaResultRecord } from "./BritannicaResultRecord";

export const BritannicaResultsPage = () => {
  const {
    searchParams: { term, page },
    setPage,
    totalHits,
    results,
    loadingResults,
    pageSize,
  } = useBritannicaSearch();
  const { translate } = useLanguage();
  const { format } = useNumberFormat();

  return (
    <Box>
      <Toolbar
        sx={{
          flexWrap: "wrap",
          gap: 1,
          pl: { sm: 1, xs: 1 },
          pr: { xs: 1, sm: 1 },
        }}
      >
        <Typography color="inherit" variant="h6" component="h2">
          {loadingResults ? (
            <Skeleton
              variant="text"
              width={20}
              sx={{ display: "inline-flex" }}
            />
          ) : (
            format(totalHits)
          )}{" "}
          {translate("searchResultsFor")} "{term}"
        </Typography>
      </Toolbar>

      {loadingResults ? (
        <>
          {Array.from({ length: pageSize }).map((_, index) => (
            <div key={index}>
              <BritannicaResultSkeleton />
            </div>
          ))}
        </>
      ) : (
        <>
          {totalHits > 0 ? (
            results.map((result) => (
              <div key={result.tocId}>
                <BritannicaResultDisplay result={result} />
              </div>
            ))
          ) : (
            <Alert severity="info">
              <AlertTitle>{translate("searchErrorMessage")}</AlertTitle>
            </Alert>
          )}
        </>
      )}
      {totalHits > pageSize && (
        <Pagination
          page={page}
          count={Math.ceil(totalHits / pageSize)}
          onChange={(_: React.ChangeEvent<unknown>, value: number) => {
            setPage(value);
          }}
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            paddingTop: "30px",
            paddingBottom: "50px",
          }}
        />
      )}
    </Box>
  );
};

export const BritannicaResultSkeleton = () => {
  return (
    <Card sx={{ mb: 2 }}>
      <CardHeader
        title={
          <Skeleton
            animation="wave"
            height={10}
            width="80%"
            style={{ marginBottom: 6 }}
          />
        }
      />
      <CardContent>
        <React.Fragment>
          <Skeleton animation="wave" height={10} style={{ marginBottom: 6 }} />
          <Skeleton animation="wave" height={10} width="80%" />
        </React.Fragment>
      </CardContent>
    </Card>
  );
};

interface BritannicaResultDisplayProps {
  result: BritannicaResultRecord;
}
export const BritannicaResultDisplay: React.FC<
  BritannicaResultDisplayProps
> = ({ result }) => {
  const { title, link, description } = result;

  //   h2 typography component
  const titleLink = useMemo(() => {
    if (link) {
      return (
        <Link href={link} target="_blank" rel="noopener noreferrer">
          {title}
        </Link>
      );
    }
    return title;
  }, [link, title]);

  return (
    <Card sx={{ mb: 2 }}>
      <CardHeader
        slotProps={{
          title: {
            component: "h3",
          },
        }}
        title={titleLink}
      />
      <CardContent>
        <Typography>{description}</Typography>
      </CardContent>
    </Card>
  );
};
